import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { add, format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem as MuiMenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip,
} from "@material-ui/core";
import {
  Edit,
  DeleteOutline,
  Facebook,
  Instagram,
  WhatsApp,
  Sync,
  MoreVert,
} from "@material-ui/icons";
import WebhookIcon from '@mui/icons-material/Webhook';
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from '../../utils/formatSerializedId';
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.padding,
    paddingLeft: 20,
    overflowY: "auto",
    ...theme.scrollbarStyles,
    background: "transparent",
    boxShadow: "none",
    border: "none",
  },
  pageSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    marginTop: 2,
  },
  connectionCard: {
    borderRadius: 16,
    boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    border: "1px solid rgba(0,0,0,0.06)",
    padding: "20px",
    background: "#fff",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
  },
  channelIconWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
    paddingTop: 2,
  },
  channelIconWA: {
    fontSize: "2.4rem",
    color: "#25D366",
  },
  channelIconFB: {
    fontSize: "2.4rem",
    color: "#3b5998",
  },
  channelIconIG: {
    fontSize: "2.4rem",
    color: "#e1306c",
  },
  oficialBadge: {
    fontSize: "0.6rem",
    height: 18,
    backgroundColor: "#1976d2",
    color: "#fff",
    "& .MuiChip-label": { padding: "0 6px" },
  },
  cardMeta: {
    flex: 1,
    minWidth: 0,
  },
  connectionName: {
    fontWeight: 700,
    fontSize: "1rem",
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  connectionPhone: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    borderRadius: 20,
    padding: "3px 10px",
    fontSize: "0.73rem",
    fontWeight: 600,
    marginTop: 6,
    width: "fit-content",
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    flexShrink: 0,
  },
  tokenBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 20,
    padding: "3px 10px",
    fontSize: "0.68rem",
    fontWeight: 600,
    marginTop: 4,
    width: "fit-content",
  },
  sessionActionsRow: {
    minHeight: 34,
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  sessionActionBtn: {
    fontSize: "0.76rem",
    borderRadius: 8,
    padding: "3px 12px",
    textTransform: "none",
    fontWeight: 600,
  },
  cardDivider: {
    borderTop: "1px solid rgba(0,0,0,0.07)",
    margin: "2px 0",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: "auto",
  },
  lastUpdate: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
  },
  cardActions: {
    display: "flex",
    alignItems: "center",
    gap: 0,
  },
  actionIconBtn: {
    padding: 6,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}15`,
    },
  },
  deleteBtn: {
    padding: 6,
    color: theme.palette.text.secondary,
    "&:hover": {
      color: "#ef4444",
      backgroundColor: "rgba(239,68,68,0.1)",
    },
  },
  skeletonCard: {
    borderRadius: 16,
    border: "1px solid rgba(0,0,0,0.06)",
    padding: "20px",
    background: "#fff",
    height: 160,
    animation: "$shimmer 1.5s infinite",
  },
  "@keyframes shimmer": {
    "0%": { opacity: 1 },
    "50%": { opacity: 0.5 },
    "100%": { opacity: 1 },
  },
  skeletonLine: {
    borderRadius: 6,
    background: "rgba(0,0,0,0.08)",
    marginBottom: 10,
  },
  addConnectionBtn: {
    borderRadius: 20,
    textTransform: "none",
    fontWeight: 600,
    padding: "7px 22px",
  },
  moreActionsBtn: {
    borderRadius: "50%",
    color: theme.palette.text.secondary,
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.06)",
    },
  },
  emptyStateWrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "80px 24px",
    textAlign: "center",
    width: "100%",
  },
  emptyStateIcon: {
    fontSize: 80,
    color: "#d1d5db",
    marginBottom: 20,
  },
  emptyStateTitle: {
    fontWeight: 700,
    fontSize: "1.25rem",
    color: theme.palette.text.primary,
    marginBottom: 8,
  },
  emptyStateSubtitle: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    marginBottom: 28,
    maxWidth: 360,
    lineHeight: 1.6,
  },
  emptyStateBtn: {
    borderRadius: 24,
    textTransform: "none",
    fontWeight: 600,
    padding: "10px 28px",
    fontSize: "0.95rem",
  },
  buttonProgress: {
    color: green[500],
  },
  tooltip: {
    backgroundColor: "#f5f5f9",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(14),
    border: "1px solid #dadde9",
    maxWidth: 450,
  },
  tooltipPopper: {
    textAlign: "center",
  },
}));

const getStatusConfig = (status) => {
  switch (status) {
    case "CONNECTED":
      return { label: "Conectado", color: "#16a34a", bg: "#dcfce7", dot: "#16a34a" };
    case "DISCONNECTED":
      return { label: "Desconectado", color: "#dc2626", bg: "#fee2e2", dot: "#dc2626" };
    case "qrcode":
      return { label: "Aguardando QR", color: "#d97706", bg: "#fef3c7", dot: "#d97706" };
    case "OPENING":
      return { label: "Conectando...", color: "#2563eb", bg: "#dbeafe", dot: "#2563eb" };
    case "TIMEOUT":
    case "PAIRING":
      return { label: "Instável", color: "#d97706", bg: "#fef3c7", dot: "#d97706" };
    default:
      return { label: status || "—", color: "#6b7280", bg: "#f3f4f6", dot: "#6b7280" };
  }
};

function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0} left={0} bottom={0} right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography variant="caption" component="div" color="textSecondary">
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

const Connections = () => {
  const classes = useStyles();

  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [channel, setChannel] = useState("whatsapp");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const history = useHistory();
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);
  const [planConfig, setPlanConfig] = useState(false);
  const [embeddedLoading, setEmbeddedLoading] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [sourceConnection, setSourceConnection] = useState("");
  const [targetConnection, setTargetConnection] = useState("");
  const [preDeleteModalOpen, setPreDeleteModalOpen] = useState(false);
  const [whatsAppToDelete, setWhatsAppToDelete] = useState(null);
  const [transferProgressModalOpen, setTransferProgressModalOpen] = useState(false);
  const [transferProgress, setTransferProgress] = useState({ current: 0, total: 0, percentage: 0 });

  const { user, socket } = useContext(AuthContext);
  const companyId = user.companyId;
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      setPlanConfig(planConfigs);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const responseFacebook = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;
      api
        .post("/facebook", { facebookUserId: id, facebookUserToken: accessToken })
        .then(() => toast.success(i18n.t("connections.facebook.success")))
        .catch((error) => toastError(error));
    }
  };

  const responseInstagram = (response) => {
    if (response.status !== "unknown") {
      const { accessToken, id } = response;
      api
        .post("/facebook", { addInstagram: true, facebookUserId: id, facebookUserToken: accessToken })
        .then(() => toast.success(i18n.t("connections.facebook.success")))
        .catch((error) => toastError(error));
    }
  };

  const launchWhatsAppEmbeddedSignup = () => {
    if (!window.FB) {
      toast.error("SDK do Facebook não carregado. Recarregue a página.");
      return;
    }

    const configId = process.env.REACT_APP_FACEBOOK_CONFIG_ID;
    if (!configId) {
      toast.error("REACT_APP_FACEBOOK_CONFIG_ID ausente. Contate o suporte.");
      return;
    }

    setEmbeddedLoading(true);

    let receivedData = null;

    const messageHandler = (event) => {
      const origin = event.origin || "";
      if (!origin.endsWith("facebook.com") && !origin.endsWith("facebook.net")) {
        return;
      }
      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (data?.type !== "WA_EMBEDDED_SIGNUP") return;

        console.log("[Embedded Signup]", data.event, data.data);

        if (data.event === "FINISH") {
          receivedData = data.data;
        } else if (data.event === "CANCEL") {
          receivedData = { cancelled: true };
        } else if (data.event === "ERROR") {
          receivedData = {
            error: data.data?.error_message || "Erro desconhecido",
          };
        }
      } catch (err) {
        console.error("[Embedded Signup] Erro parse:", err);
      }
    };

    window.addEventListener("message", messageHandler);

    // IMPORTANTE: callback do FB.login NÃO pode ser async (SDK rejeita com
    // "Expression is of type asyncfunction, not function"). Mantemos a função
    // síncrona e encadeamos a chamada à API via Promise.
    window.FB.login(
      (response) => {
        window.removeEventListener("message", messageHandler);

        if (!response.authResponse) {
          if (receivedData?.cancelled) {
            toast.info("Cadastro cancelado.");
          } else if (receivedData?.error) {
            toast.error("Erro: " + receivedData.error);
          } else {
            toast.warn("Cadastro não concluído.");
          }
          setEmbeddedLoading(false);
          return;
        }

        if (!receivedData?.phone_number_id || !receivedData?.waba_id) {
          toast.error("Dados da Meta incompletos. Tente novamente.");
          console.error("Resposta sem IDs:", { response, receivedData });
          setEmbeddedLoading(false);
          return;
        }

        const { phone_number_id, waba_id } = receivedData;

        api
          .post("/whatsapp/embedded-signup", { phone_number_id, waba_id })
          .then(() => {
            toast.success("WhatsApp Oficial conectado com sucesso!");
            // socket company-${companyId}-whatsapp atualiza a lista automaticamente
          })
          .catch((err) => toastError(err))
          .finally(() => setEmbeddedLoading(false));
      },
      {
        config_id: configId,
        response_type: "code",
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: "whatsapp_business_app_onboarding",
          sessionInfoVersion: "3",
          version: "v4",
        },
      }
    );
  };

  useEffect(() => {
    const handleWhatsappSession = (data) => {
      if (
        data.action === "update" &&
        data.session?.status === "CONNECTED" &&
        data.session?.channel === "whatsapp"
      ) {
        toast.success(
          `✅ WhatsApp conectado! A fila "Atendimento" foi criada automaticamente. Você já pode receber mensagens.`,
          { autoClose: 6000 }
        );
      }
    };

    const handleImportMessages = (data) => {
      if (data.action === "refresh") {
        setStatusImport([]);
        history.go(0);
      }
      if (data.action === "update") {
        setStatusImport(data.status);
      }
    };

    const handleTransferTickets = (data) => {
      if (data.action === "progress") {
        setTransferProgress({
          current: data.current,
          total: data.total,
          percentage: Math.round((data.current / data.total) * 100),
        });
      }
      if (data.action === "completed") {
        setTransferProgressModalOpen(false);
        setTransferProgress({ current: 0, total: 0, percentage: 0 });
        toast.success(`Transferência concluída! ${data.transferred} tickets transferidos com sucesso.`);
        handleCloseTransferModal();
      }
      if (data.action === "error") {
        setTransferProgressModalOpen(false);
        setTransferProgress({ current: 0, total: 0, percentage: 0 });
        toast.error("Erro na transferência de tickets.");
      }
    };

    socket.on(`company-${user.companyId}-whatsappSession`, handleWhatsappSession);
    socket.on(`importMessages-${user.companyId}`, handleImportMessages);
    socket.on(`transferTickets-${user.companyId}`, handleTransferTickets);

    return () => {
      socket.off(`company-${user.companyId}-whatsappSession`, handleWhatsappSession);
      socket.off(`importMessages-${user.companyId}`, handleImportMessages);
      socket.off(`transferTickets-${user.companyId}`, handleTransferTickets);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsApps]);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  // Abre o modal de QR imediatamente e inicia a sessão Baileys
  const handleConnectWhatsApp = async (whatsApp) => {
    try {
      setSelectedWhatsApp(whatsApp);
      setQrModalOpen(true);
      await api.post(`/whatsappsession/${whatsApp.id}`);
    } catch (err) {
      setQrModalOpen(false);
      setSelectedWhatsApp(null);
      toastError(err);
    }
  };

  // Chamado pelo WhatsAppModal após criar nova conexão Baileys
  const handleAfterWhatsAppSaved = (savedWhatsapp) => {
    handleConnectWhatsApp(savedWhatsapp);
  };

  const handleForceReconnect = async (whatsApp) => {
    try {
      const id = typeof whatsApp === "object" ? whatsApp.id : whatsApp;
      const target = typeof whatsApp === "object" ? whatsApp : selectedWhatsApp || { id };
      setSelectedWhatsApp(target);
      setQrModalOpen(true);
      await api.post(`/whatsapp/${id}/force-reconnect`);
    } catch (err) {
      setQrModalOpen(false);
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = (ch) => {
    setChannel(ch || "whatsapp");
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setChannel(whatsApp.channel);
    setSelectedWhatsApp(whatsApp);
    setWhatsAppModalOpen(true);
  };

  const handleSyncTemplates = async (whatsAppId) => {
    await api.get(`/whatsapp/sync-templates/${whatsAppId}`);
  };

  const handleCopyWebhook = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Webhook copiado!");
  };

  const openInNewTab = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId,
      });
    }
    if (action === "delete") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId,
      });
    }
    if (action === "closedImported") {
      setConfirmModalInfo({
        action,
        title: i18n.t("connections.confirmationModal.closedImportedTitle"),
        message: i18n.t("connections.confirmationModal.closedImportedMessage"),
        whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "closedImported") {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.closedimported"));
      } catch (err) {
        toastError(err);
      }
    }
    setConfirmModalInfo(confirmationModalInitialState);
  };

  const restartWhatsapps = async () => {
    try {
      await api.post(`/whatsapp-restart/`);
      toast.success(i18n.t("connections.waitConnection"));
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenTransferModal = () => setTransferModalOpen(true);

  const handleCloseTransferModal = () => {
    setTransferModalOpen(false);
    setSourceConnection("");
    setTargetConnection("");
  };

  const handleCloseTransferProgressModal = () => {
    setTransferProgressModalOpen(false);
    setTransferProgress({ current: 0, total: 0, percentage: 0 });
  };

  const handleTransferTickets = async () => {
    if (!sourceConnection || !targetConnection) {
      toast.error("Selecione as conexões de origem e destino");
      return;
    }
    if (sourceConnection === targetConnection) {
      toast.error("As conexões de origem e destino devem ser diferentes");
      return;
    }
    try {
      const response = await api.post(`/transfer-tickets`, {
        sourceConnectionId: sourceConnection,
        targetConnectionId: targetConnection,
      });
      if (response.data.requiresProgress) {
        setTransferModalOpen(false);
        setTransferProgressModalOpen(true);
        setTransferProgress({ current: 0, total: response.data.totalTickets, percentage: 0 });
      } else {
        toast.success(`Tickets transferidos com sucesso! ${response.data.transferred || 0} tickets transferidos.`);
        handleCloseTransferModal();
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenPreDeleteModal = (whatsAppId) => {
    setWhatsAppToDelete(whatsAppId);
    setPreDeleteModalOpen(true);
  };

  const handleClosePreDeleteModal = () => {
    setPreDeleteModalOpen(false);
    setWhatsAppToDelete(null);
  };

  const handleConfirmTransferDone = () => {
    setPreDeleteModalOpen(false);
    handleOpenConfirmationModal("delete", whatsAppToDelete);
    setWhatsAppToDelete(null);
  };

  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === "renderButtonCloseTickets") {
      return (
        <Button
          size="small"
          variant="outlined"
          color="primary"
          className={classes.sessionActionBtn}
          onClick={() => handleOpenConfirmationModal("closedImported", whatsApp.id)}
        >
          {i18n.t("connections.buttons.closedImported")}
        </Button>
      );
    }
    if (whatsApp?.importOldMessages) {
      let isTimeStamp = !isNaN(new Date(Math.floor(whatsApp?.statusImportMessages)).getTime());
      if (isTimeStamp) {
        const ultimoStatus = new Date(Math.floor(whatsApp?.statusImportMessages)).getTime();
        const dataLimite = +add(ultimoStatus, { seconds: +35 }).getTime();
        if (dataLimite > new Date().getTime()) {
          return (
            <Button
              disabled
              size="small"
              className={classes.sessionActionBtn}
              endIcon={<CircularProgress size={12} className={classes.buttonProgress} />}
              variant="outlined"
              color="primary"
            >
              {i18n.t("connections.buttons.preparing")}
            </Button>
          );
        }
      }
    }
  };

  const renderSessionActions = (whatsApp) => {
    if (whatsApp.channel !== "whatsapp") return null;
    return (
      <Can
        role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
        perform="connections-page:addConnection"
        yes={() => (
          <>
            {whatsApp.status === "qrcode" && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  className={classes.sessionActionBtn}
                  onClick={() => handleOpenQrModal(whatsApp)}
                >
                  {i18n.t("connections.buttons.qrcode")}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  className={classes.sessionActionBtn}
                  onClick={() => handleForceReconnect(whatsApp)}
                >
                  Reconectar
                </Button>
              </>
            )}
            {whatsApp.status === "DISCONNECTED" && (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  className={classes.sessionActionBtn}
                  onClick={() => handleConnectWhatsApp(whatsApp)}
                >
                  Conectar
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  className={classes.sessionActionBtn}
                  onClick={() => handleForceReconnect(whatsApp)}
                >
                  Forçar reset
                </Button>
              </>
            )}
            {(whatsApp.status === "CONNECTED" ||
              whatsApp.status === "PAIRING" ||
              whatsApp.status === "TIMEOUT") && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  className={classes.sessionActionBtn}
                  onClick={() => handleOpenConfirmationModal("disconnect", whatsApp.id)}
                >
                  {i18n.t("connections.buttons.disconnect")}
                </Button>
                {renderImportButton(whatsApp)}
              </>
            )}
            {whatsApp.status === "OPENING" && (
              <Button
                size="small"
                variant="outlined"
                disabled
                color="default"
                className={classes.sessionActionBtn}
              >
                {i18n.t("connections.buttons.connecting")}
              </Button>
            )}
          </>
        )}
      />
    );
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook className={classes.channelIconFB} />;
      case "instagram":
        return <Instagram className={classes.channelIconIG} />;
      default:
        return <WhatsApp className={classes.channelIconWA} />;
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>

      {qrModalOpen && (
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
        />
      )}

      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        onSaved={handleAfterWhatsAppSaved}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
        channel={channel}
      />

      {/* Transfer Modal */}
      <Dialog open={transferModalOpen} onClose={handleCloseTransferModal} maxWidth="sm" fullWidth>
        <DialogTitle>Transferência de Tickets</DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 24, lineHeight: 1.6 }}>
            Selecione a conexão de <strong>origem</strong> e a de <strong>destino</strong>.
            Todos os atendimentos ativos da origem serão movidos para o destino.
          </Typography>
          <div style={{ display: "flex", alignItems: "center", gap: 24, marginBottom: 24 }}>
            <FormControl fullWidth>
              <InputLabel>Origem</InputLabel>
              <Select value={sourceConnection} onChange={(e) => setSourceConnection(e.target.value)}>
                {whatsApps.map((w) => (
                  <MuiMenuItem key={w.id} value={w.id}>{w.name}</MuiMenuItem>
                ))}
              </Select>
            </FormControl>
            <div style={{ fontSize: 24, color: "#4caf50", fontWeight: "bold" }}>→</div>
            <FormControl fullWidth>
              <InputLabel>Destino</InputLabel>
              <Select value={targetConnection} onChange={(e) => setTargetConnection(e.target.value)}>
                {whatsApps.map((w) => (
                  <MuiMenuItem key={w.id} value={w.id}>{w.name}</MuiMenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTransferModal}>CANCELAR</Button>
          <Button onClick={handleTransferTickets} color="primary" variant="contained">TRANSFERIR</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Progress Modal */}
      <Dialog
        open={transferProgressModalOpen}
        onClose={handleCloseTransferProgressModal}
        maxWidth="sm"
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
      >
        <DialogTitle>Transferindo Tickets</DialogTitle>
        <DialogContent>
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Typography variant="h6" style={{ marginBottom: 16 }}>Progresso da Transferência</Typography>
            <Box position="relative" display="inline-flex" marginBottom={2}>
              <CircularProgress variant="determinate" value={transferProgress.percentage} size={80} thickness={4} />
              <Box top={0} left={0} bottom={0} right={0} position="absolute" display="flex" alignItems="center" justifyContent="center">
                <Typography variant="caption" component="div" color="textSecondary" style={{ fontSize: "14px", fontWeight: "bold" }}>
                  {transferProgress.percentage}%
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" style={{ marginTop: 16 }}>
              {transferProgress.current} de {transferProgress.total} tickets transferidos
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              Por favor, aguarde enquanto os tickets são transferidos...
            </Typography>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pre-Delete Modal */}
      <Dialog open={preDeleteModalOpen} onClose={handleClosePreDeleteModal} maxWidth="sm" fullWidth>
        <DialogTitle>Transferência de Tickets</DialogTitle>
        <DialogContent>
          <Typography variant="body1" style={{ marginBottom: 16 }}>
            Antes de excluir esta conexão, você já fez a transferência dos tickets para outra conexão?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreDeleteModal}>NÃO</Button>
          <Button onClick={handleConfirmTransferDone} color="primary" variant="contained">SIM</Button>
        </DialogActions>
      </Dialog>

      {user.profile === "user" && user.allowConnections === "disabled" ? (
        <ForbiddenPage />
      ) : (
        <>
          <MainHeader>
            <div>
              <Title>{i18n.t("connections.title")} ({whatsApps.length})</Title>
              <Typography className={classes.pageSubtitle}>
                Gerencie suas conexões WhatsApp
              </Typography>
            </div>
            <MainHeaderButtonsWrapper>
              {/* Secondary actions: MoreVert */}
              <PopupState variant="popover" popupId="secondary-actions-menu">
                {(popupState) => (
                  <>
                    <Tooltip title="Mais ações">
                      <IconButton className={classes.moreActionsBtn} {...bindTrigger(popupState)}>
                        <MoreVert />
                      </IconButton>
                    </Tooltip>
                    <Menu {...bindMenu(popupState)}>
                      <MenuItem onClick={() => { handleOpenTransferModal(); popupState.close(); }}>
                        Transferir Tickets
                      </MenuItem>
                      <MenuItem onClick={() => { restartWhatsapps(); popupState.close(); }}>
                        {i18n.t("connections.restartConnections")}
                      </MenuItem>
                      <MenuItem onClick={() => { openInNewTab(`https://wa.me/${process.env.REACT_APP_NUMBER_SUPPORT}`); popupState.close(); }}>
                        {i18n.t("connections.callSupport")}
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </PopupState>

              {/* Nova Conexão — só aparece quando há conexões (estado vazio tem seu próprio botão) */}
              {(loading || whatsApps?.length > 0) && (
                <Can
                  role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
                  perform="connections-page:addConnection"
                  yes={() => (
                    <PopupState variant="popover" popupId="new-connection-menu">
                      {(popupState) => (
                        <>
                          <Button
                            variant="contained"
                            color="primary"
                            className={classes.addConnectionBtn}
                            {...bindTrigger(popupState)}
                          >
                            + {i18n.t("connections.newConnection")}
                          </Button>
                          <Menu {...bindMenu(popupState)}>
                            <MenuItem
                              disabled={planConfig?.plan?.useWhatsapp ? false : true}
                              onClick={() => {
                                handleOpenWhatsAppModal();
                                popupState.close();
                              }}
                            >
                              <WhatsApp fontSize="small" style={{ marginRight: 10, color: "#25D366" }} />
                              WhatsApp
                            </MenuItem>
                            <MenuItem
                              disabled={!planConfig?.plan?.useWhatsappOfficial || embeddedLoading}
                              onClick={() => {
                                popupState.close();
                                launchWhatsAppEmbeddedSignup();
                              }}
                            >
                              <WhatsApp fontSize="small" style={{ marginRight: 10, color: "#25D366" }} />
                              {embeddedLoading ? "Conectando..." : "WhatsApp Oficial"}
                            </MenuItem>
                          </Menu>
                        </>
                      )}
                    </PopupState>
                  )}
                />
              )}
            </MainHeaderButtonsWrapper>
          </MainHeader>

          {/* Import progress card */}
          {statusImport?.all ? (
            <div style={{ margin: "0 0 16px" }}>
              <Card>
                <CardContent>
                  <Typography component="h5" variant="h5">
                    {statusImport?.this === -1
                      ? i18n.t("connections.buttons.preparing")
                      : i18n.t("connections.buttons.importing")}
                  </Typography>
                  {statusImport?.this === -1 ? (
                    <Typography component="h6" variant="h6" align="center">
                      <CircularProgress size={24} />
                    </Typography>
                  ) : (
                    <>
                      <Typography component="h6" variant="h6" align="center">
                        {`${i18n.t("connections.typography.processed")} ${statusImport?.this} ${i18n.t("connections.typography.in")} ${statusImport?.all}  ${i18n.t("connections.typography.date")}: ${statusImport?.date}`}
                      </Typography>
                      <Typography align="center">
                        <CircularProgressWithLabel
                          style={{ margin: "auto" }}
                          value={(statusImport?.this / statusImport?.all) * 100}
                        />
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}

          {/* Card Grid */}
          <Paper className={classes.mainPaper} variant="outlined">
            <Grid container spacing={2}>
              {loading ? (
                [1, 2, 3].map((n) => (
                  <Grid item xs={12} sm={6} md={4} key={n}>
                    <div className={classes.skeletonCard}>
                      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <div className={classes.skeletonLine} style={{ width: 40, height: 40, borderRadius: "50%", flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div className={classes.skeletonLine} style={{ width: "65%", height: 16 }} />
                          <div className={classes.skeletonLine} style={{ width: "45%", height: 13 }} />
                          <div className={classes.skeletonLine} style={{ width: "35%", height: 22, borderRadius: 20 }} />
                        </div>
                      </div>
                    </div>
                  </Grid>
                ))
              ) : whatsApps?.length > 0 ? (
                whatsApps.map((whatsApp) => {
                  const statusConfig = getStatusConfig(whatsApp.status);
                  const isOficial = whatsApp.channel === "whatsapp_oficial";
                  const phone =
                    whatsApp.channel === "whatsapp"
                      ? whatsApp.number
                        ? formatSerializedId(whatsApp.number)
                        : null
                      : whatsApp.phone_number
                      ? formatSerializedId(whatsApp.phone_number)
                      : null;

                  return (
                    <Grid item xs={12} sm={6} md={4} key={whatsApp.id}>
                      <Paper className={classes.connectionCard} elevation={0}>
                        {/* Card Header */}
                        <div className={classes.cardHeader}>
                          <div className={classes.channelIconWrapper}>
                            {getChannelIcon(whatsApp.channel)}
                            {isOficial && (
                              <Chip label="✓ Oficial" size="small" className={classes.oficialBadge} />
                            )}
                          </div>
                          <div className={classes.cardMeta}>
                            <Typography className={classes.connectionName}>{whatsApp.name}</Typography>
                            {phone && (
                              <Typography className={classes.connectionPhone}>{phone}</Typography>
                            )}
                            <div
                              className={classes.statusBadge}
                              style={{ color: statusConfig.color, backgroundColor: statusConfig.bg }}
                            >
                              <span
                                className={classes.statusDot}
                                style={{ backgroundColor: statusConfig.dot }}
                              />
                              {statusConfig.label}
                            </div>
                            {isOficial && (() => {
                              const exp = whatsApp.tokenExpiresAt;
                              if (!exp) {
                                return (
                                  <div className={classes.tokenBadge} style={{ color: "#555", backgroundColor: "#e0e0e0" }}>
                                    Token: verificando...
                                  </div>
                                );
                              }
                              // Sentinela 2099-12-31 = token permanente (SUAT / Tech Provider)
                              if (new Date(exp).getUTCFullYear() >= 2099) {
                                return (
                                  <div className={classes.tokenBadge} style={{ color: "#0d6e2e", backgroundColor: "#dcfce7" }}>
                                    Token: permanente
                                  </div>
                                );
                              }
                              const days = Math.floor((new Date(exp).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                              if (days < 0) {
                                return (
                                  <div className={classes.tokenBadge} style={{ color: "#fff", backgroundColor: "#c62828" }}>
                                    Token EXPIRADO
                                  </div>
                                );
                              }
                              if (days <= 3) {
                                return (
                                  <div className={classes.tokenBadge} style={{ color: "#fff", backgroundColor: "#e53935" }}>
                                    Token expirando! ({days}d)
                                  </div>
                                );
                              }
                              if (days <= 14) {
                                return (
                                  <div className={classes.tokenBadge} style={{ color: "#7c4500", backgroundColor: "#fff3e0" }}>
                                    Token expira em {days} dias
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>

                        {/* Session action buttons */}
                        <div className={classes.sessionActionsRow}>
                          {renderSessionActions(whatsApp)}
                        </div>

                        <div className={classes.cardDivider} />

                        {/* Card Footer: last update + action icons */}
                        <div className={classes.cardFooter}>
                          <Typography className={classes.lastUpdate}>
                            {format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
                          </Typography>
                          <Can
                            role={user.profile}
                            perform="connections-page:addConnection"
                            yes={() => (
                              <div className={classes.cardActions}>
                                <Tooltip title="Editar">
                                  <IconButton
                                    size="small"
                                    className={classes.actionIconBtn}
                                    onClick={() => handleEditWhatsApp(whatsApp)}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {isOficial && (
                                  <>
                                    <Tooltip title="Sincronizar templates">
                                      <IconButton
                                        size="small"
                                        className={classes.actionIconBtn}
                                        onClick={() => handleSyncTemplates(whatsApp.id)}
                                      >
                                        <Sync fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Copiar webhook para Meta">
                                      <IconButton
                                        size="small"
                                        className={classes.actionIconBtn}
                                        onClick={() => handleCopyWebhook(whatsApp.waba_webhook)}
                                      >
                                        <WebhookIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                                <Tooltip title="Excluir">
                                  <IconButton
                                    size="small"
                                    className={classes.deleteBtn}
                                    onClick={() => handleOpenPreDeleteModal(whatsApp.id)}
                                  >
                                    <DeleteOutline fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            )}
                          />
                        </div>
                      </Paper>
                    </Grid>
                  );
                })
              ) : (
                <Grid item xs={12}>
                  <div className={classes.emptyStateWrapper}>
                    <WhatsApp className={classes.emptyStateIcon} />
                    <Typography className={classes.emptyStateTitle}>
                      Nenhuma conexão ainda
                    </Typography>
                    <Typography className={classes.emptyStateSubtitle}>
                      Conecte seu WhatsApp para começar a atender seus clientes
                    </Typography>
                    <Can
                      role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
                      perform="connections-page:addConnection"
                      yes={() => (
                        <PopupState variant="popover" popupId="empty-state-connection-menu">
                          {(popupState) => (
                            <>
                              <Button
                                variant="contained"
                                color="primary"
                                className={classes.emptyStateBtn}
                                {...bindTrigger(popupState)}
                              >
                                + Nova Conexão
                              </Button>
                              <Menu {...bindMenu(popupState)}>
                                <MenuItem
                                  disabled={planConfig?.plan?.useWhatsapp ? false : true}
                                  onClick={() => {
                                    handleOpenWhatsAppModal();
                                    popupState.close();
                                  }}
                                >
                                  <WhatsApp fontSize="small" style={{ marginRight: 10, color: "#25D366" }} />
                                  WhatsApp
                                </MenuItem>
                                <MenuItem
                                  disabled={!planConfig?.plan?.useWhatsappOfficial || embeddedLoading}
                                  onClick={() => {
                                    popupState.close();
                                    launchWhatsAppEmbeddedSignup();
                                  }}
                                >
                                  <WhatsApp fontSize="small" style={{ marginRight: 10, color: "#25D366" }} />
                                  {embeddedLoading ? "Conectando..." : "WhatsApp Oficial"}
                                </MenuItem>
                              </Menu>
                            </>
                          )}
                        </PopupState>
                      )}
                    />
                  </div>
                </Grid>
              )}
            </Grid>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default Connections;
